import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { z } from "zod";
import { Scale, Ruler } from "lucide-react";

interface AddMeasurementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  clientId: string;
  existingMeasurement?: {
    id: string;
    weight_lbs: number | null;
    body_fat_percent: number | null;
    chest_inches: number | null;
    waist_inches: number | null;
    hips_inches: number | null;
    left_arm_inches: number | null;
    right_arm_inches: number | null;
    left_thigh_inches: number | null;
    right_thigh_inches: number | null;
    left_calf_inches: number | null;
    right_calf_inches: number | null;
    neck_inches: number | null;
    notes: string | null;
  };
}

const measurementSchema = z.object({
  weight_lbs: z.number().min(50).max(700).optional().nullable(),
  body_fat_percent: z.number().min(1).max(70).optional().nullable(),
  chest_inches: z.number().min(20).max(80).optional().nullable(),
  waist_inches: z.number().min(15).max(80).optional().nullable(),
  hips_inches: z.number().min(20).max(80).optional().nullable(),
  left_arm_inches: z.number().min(5).max(30).optional().nullable(),
  right_arm_inches: z.number().min(5).max(30).optional().nullable(),
  left_thigh_inches: z.number().min(10).max(50).optional().nullable(),
  right_thigh_inches: z.number().min(10).max(50).optional().nullable(),
  left_calf_inches: z.number().min(8).max(30).optional().nullable(),
  right_calf_inches: z.number().min(8).max(30).optional().nullable(),
  neck_inches: z.number().min(8).max(30).optional().nullable(),
  notes: z.string().max(500).optional().nullable(),
});

const AddMeasurementDialog = ({
  open,
  onOpenChange,
  onSuccess,
  clientId,
  existingMeasurement,
}: AddMeasurementDialogProps) => {
  const [weightLbs, setWeightLbs] = useState("");
  const [bodyFatPercent, setBodyFatPercent] = useState("");
  const [chestInches, setChestInches] = useState("");
  const [waistInches, setWaistInches] = useState("");
  const [hipsInches, setHipsInches] = useState("");
  const [leftArmInches, setLeftArmInches] = useState("");
  const [rightArmInches, setRightArmInches] = useState("");
  const [leftThighInches, setLeftThighInches] = useState("");
  const [rightThighInches, setRightThighInches] = useState("");
  const [leftCalfInches, setLeftCalfInches] = useState("");
  const [rightCalfInches, setRightCalfInches] = useState("");
  const [neckInches, setNeckInches] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open && existingMeasurement) {
      setWeightLbs(existingMeasurement.weight_lbs?.toString() || "");
      setBodyFatPercent(existingMeasurement.body_fat_percent?.toString() || "");
      setChestInches(existingMeasurement.chest_inches?.toString() || "");
      setWaistInches(existingMeasurement.waist_inches?.toString() || "");
      setHipsInches(existingMeasurement.hips_inches?.toString() || "");
      setLeftArmInches(existingMeasurement.left_arm_inches?.toString() || "");
      setRightArmInches(existingMeasurement.right_arm_inches?.toString() || "");
      setLeftThighInches(existingMeasurement.left_thigh_inches?.toString() || "");
      setRightThighInches(existingMeasurement.right_thigh_inches?.toString() || "");
      setLeftCalfInches(existingMeasurement.left_calf_inches?.toString() || "");
      setRightCalfInches(existingMeasurement.right_calf_inches?.toString() || "");
      setNeckInches(existingMeasurement.neck_inches?.toString() || "");
      setNotes(existingMeasurement.notes || "");
    } else if (open) {
      resetForm();
    }
  }, [open, existingMeasurement]);

  const resetForm = () => {
    setWeightLbs("");
    setBodyFatPercent("");
    setChestInches("");
    setWaistInches("");
    setHipsInches("");
    setLeftArmInches("");
    setRightArmInches("");
    setLeftThighInches("");
    setRightThighInches("");
    setLeftCalfInches("");
    setRightCalfInches("");
    setNeckInches("");
    setNotes("");
  };

  const parseOptionalNumber = (value: string): number | null => {
    if (!value || value.trim() === "") return null;
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check at least one measurement is provided
    const hasAnyMeasurement =
      weightLbs || bodyFatPercent || chestInches || waistInches || hipsInches ||
      leftArmInches || rightArmInches || leftThighInches || rightThighInches ||
      leftCalfInches || rightCalfInches || neckInches;

    if (!hasAnyMeasurement) {
      toast.error("Please enter at least one measurement");
      return;
    }

    try {
      const data = {
        weight_lbs: parseOptionalNumber(weightLbs),
        body_fat_percent: parseOptionalNumber(bodyFatPercent),
        chest_inches: parseOptionalNumber(chestInches),
        waist_inches: parseOptionalNumber(waistInches),
        hips_inches: parseOptionalNumber(hipsInches),
        left_arm_inches: parseOptionalNumber(leftArmInches),
        right_arm_inches: parseOptionalNumber(rightArmInches),
        left_thigh_inches: parseOptionalNumber(leftThighInches),
        right_thigh_inches: parseOptionalNumber(rightThighInches),
        left_calf_inches: parseOptionalNumber(leftCalfInches),
        right_calf_inches: parseOptionalNumber(rightCalfInches),
        neck_inches: parseOptionalNumber(neckInches),
        notes: notes || null,
      };

      measurementSchema.parse(data);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      setIsSubmitting(true);

      if (existingMeasurement) {
        const { error } = await supabase
          .from("client_measurements")
          .update(data)
          .eq("id", existingMeasurement.id);

        if (error) throw error;
        toast.success("Measurement updated");
      } else {
        const { error } = await supabase.from("client_measurements").insert({
          ...data,
          client_id: clientId,
          user_id: user.id,
        });

        if (error) throw error;
        toast.success("Measurement recorded");
      }

      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        console.error("Error saving measurement:", error);
        toast.error("Failed to save measurement");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existingMeasurement ? "Edit Measurement" : "Record Measurement"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Tabs defaultValue="core" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="core" className="gap-2">
                <Scale className="h-4 w-4" />
                Core
              </TabsTrigger>
              <TabsTrigger value="body" className="gap-2">
                <Ruler className="h-4 w-4" />
                Body
              </TabsTrigger>
            </TabsList>

            <TabsContent value="core" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="weight">Weight (lbs)</Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.1"
                    min="50"
                    max="700"
                    placeholder="185.0"
                    value={weightLbs}
                    onChange={(e) => setWeightLbs(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bodyfat">Body Fat %</Label>
                  <Input
                    id="bodyfat"
                    type="number"
                    step="0.1"
                    min="1"
                    max="70"
                    placeholder="18.5"
                    value={bodyFatPercent}
                    onChange={(e) => setBodyFatPercent(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="chest">Chest (in)</Label>
                  <Input
                    id="chest"
                    type="number"
                    step="0.1"
                    min="20"
                    max="80"
                    placeholder="42.0"
                    value={chestInches}
                    onChange={(e) => setChestInches(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="waist">Waist (in)</Label>
                  <Input
                    id="waist"
                    type="number"
                    step="0.1"
                    min="15"
                    max="80"
                    placeholder="34.0"
                    value={waistInches}
                    onChange={(e) => setWaistInches(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hips">Hips (in)</Label>
                  <Input
                    id="hips"
                    type="number"
                    step="0.1"
                    min="20"
                    max="80"
                    placeholder="40.0"
                    value={hipsInches}
                    onChange={(e) => setHipsInches(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="neck">Neck (in)</Label>
                <Input
                  id="neck"
                  type="number"
                  step="0.1"
                  min="8"
                  max="30"
                  placeholder="16.0"
                  value={neckInches}
                  onChange={(e) => setNeckInches(e.target.value)}
                />
              </div>
            </TabsContent>

            <TabsContent value="body" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Arms (inches)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="leftArm">Left Arm</Label>
                    <Input
                      id="leftArm"
                      type="number"
                      step="0.1"
                      min="5"
                      max="30"
                      placeholder="14.5"
                      value={leftArmInches}
                      onChange={(e) => setLeftArmInches(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rightArm">Right Arm</Label>
                    <Input
                      id="rightArm"
                      type="number"
                      step="0.1"
                      min="5"
                      max="30"
                      placeholder="14.5"
                      value={rightArmInches}
                      onChange={(e) => setRightArmInches(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Thighs (inches)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="leftThigh">Left Thigh</Label>
                    <Input
                      id="leftThigh"
                      type="number"
                      step="0.1"
                      min="10"
                      max="50"
                      placeholder="24.0"
                      value={leftThighInches}
                      onChange={(e) => setLeftThighInches(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rightThigh">Right Thigh</Label>
                    <Input
                      id="rightThigh"
                      type="number"
                      step="0.1"
                      min="10"
                      max="50"
                      placeholder="24.0"
                      value={rightThighInches}
                      onChange={(e) => setRightThighInches(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Calves (inches)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="leftCalf">Left Calf</Label>
                    <Input
                      id="leftCalf"
                      type="number"
                      step="0.1"
                      min="8"
                      max="30"
                      placeholder="15.0"
                      value={leftCalfInches}
                      onChange={(e) => setLeftCalfInches(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rightCalf">Right Calf</Label>
                    <Input
                      id="rightCalf"
                      type="number"
                      step="0.1"
                      min="8"
                      max="30"
                      placeholder="15.0"
                      value={rightCalfInches}
                      onChange={(e) => setRightCalfInches(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any notes about this measurement..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Saving..."
                : existingMeasurement
                ? "Update"
                : "Save Measurement"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMeasurementDialog;
